import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NotificationService } from '../../../../../common/services';
import {
  LIST_SERVER_PAGINATION_INITIAL_STATE,
  ListServerPaginationStateModel,
  MeasurementUnitCodesEnum,
  UnitSystemListModel,
} from '../../../../../common/models';
import { ShipmentTransportOrderMaterialsService } from './shipment-transport-order-materials.service';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants';
import {
  ShipmentTransportOrderMaterialsInitialState,
  ShipmentTransportOrderMaterialsLoad,
} from './shipment-transport-order-materials.actions';
import { ShipmentTransportOrderMaterialModel } from '../../models/shipment-transport-order-materials.model';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type ShipmentTransportOrderMaterialsStateModel =
  ListServerPaginationStateModel<ShipmentTransportOrderMaterialModel> & UnitSystemListModel;

const initialState: ShipmentTransportOrderMaterialsStateModel = {
  ...LIST_SERVER_PAGINATION_INITIAL_STATE,
  dimensionsUnitCode: null,
  weightUnitCode: null,
  volumeUnitCode: null,
};

@State<ShipmentTransportOrderMaterialsStateModel>({
  name: 'shipmentTransportOrderMaterials',
  defaults: initialState,
})
@Injectable()
export class ShipmentTransportOrderMaterialsState {
  constructor(
    private shipmentTransportOrderMaterialsService: ShipmentTransportOrderMaterialsService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: ShipmentTransportOrderMaterialsStateModel): ShipmentTransportOrderMaterialsStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ShipmentTransportOrderMaterialsStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getMaterials(state: ShipmentTransportOrderMaterialsStateModel): ShipmentTransportOrderMaterialModel[] {
    return state.items;
  }

  @Selector()
  static getDimensionsUnitCode(state: ShipmentTransportOrderMaterialsStateModel): MeasurementUnitCodesEnum {
    return state.dimensionsUnitCode;
  }

  @Selector()
  static getWeightUnitCode(state: ShipmentTransportOrderMaterialsStateModel): MeasurementUnitCodesEnum {
    return state.weightUnitCode;
  }

  @Selector()
  static getVolumeUnitCode(state: ShipmentTransportOrderMaterialsStateModel): MeasurementUnitCodesEnum {
    return state.volumeUnitCode;
  }

  @Action(ShipmentTransportOrderMaterialsLoad)
  getTransportOrderMaterials(
    ctx: StateContext<ShipmentTransportOrderMaterialsStateModel>,
    { payload }: ShipmentTransportOrderMaterialsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentTransportOrderMaterialsService.getShipmentTransportOrderMaterials(payload).pipe(
      map((state: ShipmentTransportOrderMaterialsStateModel) => {
        ctx.patchState({ ...state, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentTransportOrderMaterialsInitialState)
  setInitialState(ctx: StateContext<ShipmentTransportOrderMaterialsStateModel>): void {
    ctx.setState(initialState);
  }
}
